// ============================================
// Accounts List Page - Based on approved views/accounts.html
// ============================================

import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { toast } from 'sonner';
import { AppLayout } from '@/components/AppLayout';
import { TenantBadge } from '@/components/TenantBadge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { DataTableSkeleton } from '@/components/skeletons';
import { Skeleton } from '@/components/ui/skeleton';
import { useTenant } from '@/hooks/useTenant';
import { buildTenantDisplayMap, getTenantDisplayById } from '@/lib/tenant-display';
import { ACCOUNT_TYPE, type AccountType, type Industry } from '@muraka/shared';
import { EmptyState } from '@/components/ui/empty-state';
import { Plus, Search, Loader2, Eye, Trash2, Building2 } from 'lucide-react';

const typeConfig: Record<AccountType, { label: string; class: string }> = {
  prospect: { label: 'Prospect', class: 'bg-blue-100 text-blue-700' },
  customer: { label: 'Customer', class: 'bg-green-100 text-green-700' },
  partner: { label: 'Partner', class: 'bg-amber-100 text-amber-700' },
  competitor: { label: 'Competitor', class: 'bg-red-100 text-red-700' },
};

const industryLabels: Record<Industry, string> = {
  hotel: 'Hotel',
  resort: 'Resort',
  guesthouse: 'Guest House',
  safari_vessel: 'Safari Vessel',
  cruise: 'Cruise',
  restaurant: 'Restaurant',
  government: 'Government',
  healthcare: 'Healthcare',
  retail: 'Retail',
  corporate: 'Corporate',
  distributor: 'Distributor',
  other: 'Other',
};

const iconColors = [
  'bg-blue-100 text-blue-600',
  'bg-green-100 text-green-600',
  'bg-amber-100 text-amber-600',
  'bg-purple-100 text-purple-600',
  'bg-red-100 text-red-600',
  'bg-pink-100 text-pink-600',
];

function getIconColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return iconColors[Math.abs(hash) % iconColors.length];
}

export function AccountsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const searchParams = useSearch({ from: '/accounts' });
  const { isTenantContextReady, activeTenantId, mode, tenantList } = useTenant();

  // State
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<AccountType | ''>('');
  const [industryFilter, setIndustryFilter] = useState<Industry | ''>('');
  const [createModalOpen, setCreateModalOpen] = useState(() => searchParams.create === 'true');

  // Clear search param from URL after modal opens
  useEffect(() => {
    if (searchParams.create === 'true') {
      navigate({ to: '/accounts', search: {}, replace: true });
    }
  }, [searchParams.create, navigate]);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    type: ACCOUNT_TYPE.PROSPECT as AccountType,
    industry: '' as Industry | '',
    website: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    notes: '',
  });

  // Query accounts
  const { data, isLoading, error } = useQuery({
    queryKey: ['accounts', mode, activeTenantId, page, search, typeFilter, industryFilter],
    queryFn: () =>
      api.getAccounts({
        page,
        pageSize: 20,
        search: search || undefined,
        type: typeFilter || undefined,
        industry: industryFilter || undefined,
      }),
    enabled: isTenantContextReady,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: api.deleteAccount.bind(api),
    onSuccess: () => {
      toast.success('Account deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
    onError: error => {
      toast.error(error instanceof Error ? error.message : 'Failed to delete account');
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: api.createAccount.bind(api),
    onSuccess: () => {
      toast.success('Account created successfully');
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      setCreateModalOpen(false);
      resetForm();
    },
    onError: error => {
      toast.error(error instanceof Error ? error.message : 'Failed to create account');
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      type: ACCOUNT_TYPE.PROSPECT,
      industry: '',
      website: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      country: '',
      postalCode: '',
      notes: '',
    });
  };

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate({
      name: formData.name,
      type: formData.type,
      industry: formData.industry || null,
      website: formData.website || null,
      phone: formData.phone || null,
      address: formData.address || null,
      city: formData.city || null,
      state: formData.state || null,
      country: formData.country || null,
      postalCode: formData.postalCode || null,
      notes: formData.notes || null,
    });
  };

  const handleDelete = (account: { id: string; name: string }) => {
    if (confirm(`Are you sure you want to delete ${account.name}?`)) {
      deleteMutation.mutate(account.id);
    }
  };

  const accounts = data?.data || [];
  const pagination = data?.pagination;
  const showTenantColumn = mode === 'all';
  const tenantDisplayMap = useMemo(() => buildTenantDisplayMap(tenantList), [tenantList]);

  // Calculate type counts from current filtered data
  const getTypeCount = (type: AccountType) => {
    return accounts.filter(a => a.type === type).length;
  };

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Accounts</h1>
            <p className="text-muted-foreground mt-1">Manage your customers and partners</p>
          </div>
          <Button onClick={() => setCreateModalOpen(true)} data-testid="new-account-button">
            <Plus className="h-4 w-4" />
            New Account
          </Button>
        </div>

        {/* Filters & Search Bar */}
        <div className="bg-card rounded-xl border border-border">
          <div className="p-4 flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search accounts..."
                value={search}
                onChange={e => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="pl-10"
              />
            </div>

            {/* Filters */}
            <div className="flex gap-2">
              <Select
                value={typeFilter}
                onChange={e => {
                  setTypeFilter(e.target.value as AccountType | '');
                  setPage(1);
                }}
                className="w-auto"
              >
                <option value="">All Types</option>
                {Object.entries(typeConfig).map(([value, config]) => (
                  <option key={value} value={value}>
                    {config.label}
                  </option>
                ))}
              </Select>

              <Select
                value={industryFilter}
                onChange={e => {
                  setIndustryFilter(e.target.value as Industry | '');
                  setPage(1);
                }}
                className="hidden sm:block w-auto"
              >
                <option value="">All Industries</option>
                {Object.entries(industryLabels).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          {/* Type Pills (Quick Filters) */}
          <div className="px-4 pb-4 flex flex-wrap gap-2">
            <button
              onClick={() => {
                setTypeFilter('');
                setPage(1);
              }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                typeFilter === ''
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              All ({accounts.length})
            </button>
            {Object.entries(typeConfig).map(([value, config]) => (
              <button
                key={value}
                onClick={() => {
                  setTypeFilter(value as AccountType);
                  setPage(1);
                }}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  typeFilter === value
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {config.label}s ({getTypeCount(value as AccountType)})
              </button>
            ))}
          </div>
        </div>

        {/* Accounts Table (Desktop) */}
        <div className="hidden lg:block bg-card rounded-xl border border-border overflow-hidden">
          {isLoading ? (
            <div className="p-4">
              <DataTableSkeleton columnCount={showTenantColumn ? 6 : 5} rowCount={8} />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-destructive mb-2">Failed to load accounts</p>
              <p className="text-sm text-muted-foreground">Please try again later</p>
            </div>
          ) : accounts.length === 0 ? (
            search || typeFilter || industryFilter ? (
              <EmptyState
                title="No accounts found"
                description="Try adjusting your search or filters"
                actionLabel="Clear Query"
                onAction={() => {
                  setSearch('');
                  setTypeFilter('');
                  setIndustryFilter('');
                  setPage(1);
                }}
                icon={Search}
              />
            ) : (
              <EmptyState
                title="No accounts yet"
                description="Create accounts to track organizations you do business with."
                actionLabel="Create Account"
                onAction={() => setCreateModalOpen(true)}
                icon={Building2}
              />
            )
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">
                    Account Name
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">
                    Type
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">
                    Industry
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">
                    Phone
                  </th>
                  {showTenantColumn && (
                    <th className="w-[180px] text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">
                      Tenant
                    </th>
                  )}
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {accounts.map(account => {
                  const typeInfo = typeConfig[account.type as AccountType];
                  const tenantDisplay = getTenantDisplayById(account.tenantId, tenantDisplayMap);

                  return (
                    <tr
                      key={account.id}
                      className="hover:bg-muted/50 transition-colors cursor-pointer"
                      onClick={() => navigate({ to: '/accounts/$id', params: { id: account.id } })}
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-10 h-10 rounded-lg flex items-center justify-center ${getIconColor(account.name)}`}
                          >
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                              />
                            </svg>
                          </div>
                          <div>
                            <p className="font-medium text-foreground">{account.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {account.website
                                ? account.website.replace('https://', '').replace('http://', '')
                                : '—'}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${typeInfo.class}`}
                        >
                          {typeInfo.label}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-sm text-foreground">
                        {account.industry ? industryLabels[account.industry as Industry] : '—'}
                      </td>
                      <td className="px-5 py-4 text-sm text-muted-foreground">
                        {account.phone || '—'}
                      </td>
                      {showTenantColumn && (
                        <td className="px-5 py-4">
                          <TenantBadge
                            tenantName={tenantDisplay.name}
                            brandColor={tenantDisplay.brandColor}
                            density="compact"
                          />
                        </td>
                      )}
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                          <button
                            onClick={() =>
                              navigate({ to: '/accounts/$id', params: { id: account.id } })
                            }
                            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted"
                            title="View"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(account)}
                            className="p-1.5 rounded-lg text-destructive hover:bg-destructive/10"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* Pagination */}
          {pagination && (
            <div className="px-5 py-4 border-t border-border flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing{' '}
                <span className="font-medium text-foreground">
                  {Math.min((page - 1) * pagination.pageSize + 1, pagination.total)}
                </span>{' '}
                to{' '}
                <span className="font-medium text-foreground">
                  {Math.min(page * pagination.pageSize, pagination.total)}
                </span>{' '}
                of <span className="font-medium text-foreground">{pagination.total}</span> accounts
              </p>
              {pagination.totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => p - 1)}
                    disabled={page === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => p + 1)}
                    disabled={page === pagination.totalPages}
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Accounts Cards (Mobile/Tablet) */}
        <div className="lg:hidden space-y-3">
          {isLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton
                  key={i}
                  className="h-[140px] w-full rounded-xl bg-card border border-border"
                />
              ))}
            </div>
          ) : accounts.length === 0 ? (
            search || typeFilter || industryFilter ? (
              <EmptyState
                title="No accounts found"
                description="Try adjusting your search or filters"
                actionLabel="Clear Query"
                onAction={() => {
                  setSearch('');
                  setTypeFilter('');
                  setIndustryFilter('');
                  setPage(1);
                }}
                icon={Search}
              />
            ) : (
              <EmptyState
                title="No accounts yet"
                description="Create accounts to track organizations you do business with."
                actionLabel="Create Account"
                onAction={() => setCreateModalOpen(true)}
                icon={Building2}
              />
            )
          ) : (
            accounts.map(account => {
              const typeInfo = typeConfig[account.type as AccountType];
              const tenantDisplay = getTenantDisplayById(account.tenantId, tenantDisplayMap);

              return (
                <div
                  key={account.id}
                  className="bg-card rounded-xl border border-border p-4 hover:border-primary/30 transition-colors"
                >
                  <div
                    className="flex items-start gap-3"
                    onClick={() => navigate({ to: '/accounts/$id', params: { id: account.id } })}
                  >
                    <div
                      className={`w-12 h-12 rounded-lg flex items-center justify-center shrink-0 ${getIconColor(account.name)}`}
                    >
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                        />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-medium text-foreground">{account.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {account.industry
                              ? industryLabels[account.industry as Industry]
                              : 'No industry'}
                          </p>
                        </div>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium shrink-0 ${typeInfo.class}`}
                        >
                          {typeInfo.label}
                        </span>
                      </div>
                      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
                        {showTenantColumn && (
                          <TenantBadge
                            tenantName={tenantDisplay.name}
                            brandColor={tenantDisplay.brandColor}
                            className="max-w-[12rem]"
                          />
                        )}
                        {account.phone && (
                          <span className="flex items-center gap-1">
                            <svg
                              className="w-3.5 h-3.5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                              />
                            </svg>
                            <span>{account.phone}</span>
                          </span>
                        )}
                        {account.website && (
                          <span className="flex items-center gap-1">
                            <svg
                              className="w-3.5 h-3.5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                              />
                            </svg>
                            <span>
                              {account.website.replace('https://', '').replace('http://', '')}
                            </span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Mobile Action Buttons */}
                  <div className="mt-3 pt-3 border-t border-border flex items-center justify-end gap-2">
                    <button
                      onClick={() => navigate({ to: '/accounts/$id', params: { id: account.id } })}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-foreground bg-muted rounded-lg hover:bg-muted/80 transition-colors"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      View
                    </button>
                    <button
                      onClick={() => handleDelete(account)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-destructive bg-destructive/10 rounded-lg hover:bg-destructive/20 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Delete
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Create Account Modal */}
      <Dialog open={createModalOpen} onClose={() => setCreateModalOpen(false)}>
        <DialogContent onClose={() => setCreateModalOpen(false)} className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>New Account</DialogTitle>
            <DialogDescription>Add a new customer or partner account</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate}>
            <div className="space-y-6 p-6 pt-0 max-h-[calc(100vh-200px)] overflow-y-auto">
              {/* Account Information */}
              <div>
                <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-muted-foreground"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                  Account Information
                </h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Input
                    label="Account Name"
                    required
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Acme Corporation"
                    className="sm:col-span-2"
                    data-testid="account-name-input"
                  />
                  <Select
                    label="Account Type"
                    required
                    value={formData.type}
                    onChange={e =>
                      setFormData({ ...formData, type: e.target.value as AccountType })
                    }
                  >
                    {Object.entries(typeConfig).map(([value, config]) => (
                      <option key={value} value={value}>
                        {config.label}
                      </option>
                    ))}
                  </Select>
                  <Select
                    label="Industry"
                    value={formData.industry}
                    onChange={e =>
                      setFormData({ ...formData, industry: e.target.value as Industry })
                    }
                  >
                    <option value="">Select Industry</option>
                    {Object.entries(industryLabels).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>

              {/* Contact Information */}
              <div>
                <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-muted-foreground"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                  Contact Information
                </h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Input
                    label="Website"
                    type="url"
                    value={formData.website}
                    onChange={e => setFormData({ ...formData, website: e.target.value })}
                    placeholder="https://company.com"
                    className="sm:col-span-2"
                  />
                  <Input
                    label="Phone"
                    value={formData.phone}
                    onChange={e => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>

              {/* Address */}
              <div>
                <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-muted-foreground"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  Address
                </h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Input
                    label="Street Address"
                    value={formData.address}
                    onChange={e => setFormData({ ...formData, address: e.target.value })}
                    placeholder="123 Main Street"
                    className="sm:col-span-2"
                  />
                  <Input
                    label="City"
                    value={formData.city}
                    onChange={e => setFormData({ ...formData, city: e.target.value })}
                    placeholder="San Francisco"
                  />
                  <Input
                    label="State/Province"
                    value={formData.state}
                    onChange={e => setFormData({ ...formData, state: e.target.value })}
                    placeholder="California"
                  />
                  <Input
                    label="Country"
                    value={formData.country}
                    onChange={e => setFormData({ ...formData, country: e.target.value })}
                    placeholder="United States"
                  />
                  <Input
                    label="Postal Code"
                    value={formData.postalCode}
                    onChange={e => setFormData({ ...formData, postalCode: e.target.value })}
                    placeholder="94102"
                  />
                </div>
              </div>

              {/* Notes */}
              <div>
                <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-muted-foreground"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                  Notes
                </h3>
                <Textarea
                  rows={3}
                  value={formData.notes}
                  onChange={e => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes about this account..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setCreateModalOpen(false)}
                data-testid="create-account-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createMutation.isPending}
                data-testid="create-account-submit"
              >
                {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Create Account
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
